import { useState } from "react";

import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import {
  packzettelDokument,
  useDeletePackzettel,
  usePackzettel,
} from "../../hooks/usePackzettel";
import { errorText, euro } from "../../lib/invoices";
import { tagDe } from "../../lib/kundeAktivitaet";

/**
 * Die Packzettel eines Angebots — mit der Marge, die bei uns bleibt.
 *
 * **Das ist die einzige Stelle, an der die Marge steht.** Auf dem Packzettel
 * selbst taucht sie nicht auf, und auch unser Angebotspreis nicht: der Zettel
 * liegt beim Subunternehmer.
 *
 * Wird sowohl im Editor als auch in den Angebotsdetails eingehängt; beide
 * fragen dieselbe Route, nur über einen anderen Bezug (Entwurf bzw. Beleg).
 */

export type PackzettelListeProps = {
  entwurfId?: string;
  rechnungId?: string;
};

export default function PackzettelListe({ entwurfId, rechnungId }: PackzettelListeProps) {
  const { data, isLoading } = usePackzettel(entwurfId, rechnungId);
  const loeschen = useDeletePackzettel();
  const [meldung, setMeldung] = useState("");
  const [dok, setDok] = useState<Dokument>(null);
  const [laedt, setLaedt] = useState("");

  const zettel = data?.items || [];
  // Kein Zettel, kein Abschnitt — eine leere Überschrift beantwortet nichts.
  if (isLoading || zettel.length === 0) return null;

  const oeffnen = async (id: string, nummer: string) => {
    setMeldung("");
    setLaedt(id);
    try {
      setDok(await packzettelDokument(id, nummer));
    } catch (fehler) {
      setMeldung(errorText(fehler));
    } finally {
      setLaedt("");
    }
  };
  const schliessen = () => {
    if (dok?.url.startsWith("blob:")) URL.revokeObjectURL(dok.url);
    setDok(null);
  };

  return (
    <section className="invoice-pz-liste">
      <div className="invoice-pz-liste-kopf">
        <h3>Packzettel</h3>
        <span>{zettel.length === 1 ? "1 Zettel" : `${zettel.length} Zettel`}</span>
      </div>

      {meldung && <p className="invoice-pz-fehler" role="alert">{meldung}</p>}

      <ul>
        {zettel.map((z) => (
          <li key={z.id}>
            <div className="invoice-pz-liste-zeile">
              <span className="invoice-pz-liste-text">
                <strong>{z.nummer}</strong>
                <small>
                  {z.empfaenger.split("\n")[0] || "Ohne Empfänger"}
                  {" · "}{z.positionen.length === 1 ? "1 Position" : `${z.positionen.length} Positionen`}
                  {z.created_at && ` · ${tagDe(z.created_at.slice(0, 10))}`}
                  {!z.budget_zeigen && " · ohne Budget gedruckt"}
                </small>
              </span>
              <span
                className={"invoice-pz-liste-marge" + (z.unter_mindestmarge ? " is-warn" : "")}
                title={`Angebot ${euro(z.angebot_netto)} − Budget ${euro(z.budget_netto)}`}
              >
                <b>{z.bewertbar ? `${z.marge_prozent} %` : "—"}</b>
                <small>{euro(z.marge_netto)}</small>
              </span>
              <span className="invoice-pz-liste-tools">
                <button type="button" className="invoice-button" onClick={() => oeffnen(z.id, z.nummer)} disabled={laedt === z.id}>
                  {laedt === z.id ? "Lädt …" : "Ansehen"}
                </button>
                <button
                  type="button"
                  className="invoice-line-remove"
                  onClick={() => loeschen.mutate(z.id)}
                  aria-label={`Packzettel ${z.nummer} löschen`}
                >
                  ×
                </button>
              </span>
            </div>
            {z.unter_mindestmarge && (
              <p className="invoice-pz-liste-warn">
                Marge unter {z.mindestmarge} % — Angebot {euro(z.angebot_netto)} netto,
                zugesagtes Budget {euro(z.budget_netto)} netto.
              </p>
            )}
          </li>
        ))}
      </ul>
      <DokumentLightbox dok={dok} onClose={schliessen} />
    </section>
  );
}
